import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { ScrollReveal } from "../components/Reveal";

const expertise = [
  {
    heading: "UK Services",
    items: [
      "Visitor Visa Application Support",
      "Student Visa Application Support",
      "Skilled Worker Visa Documentation",
      "Family & Partner Visa Applications",
      "British Citizenship Applications",
      "Indefinite Leave to Remain (ILR)",
      "British Passport Applications & Renewals",
    ],
  },
  {
    heading: "Indian Services",
    items: [
      "OCI Applications",
      "Indian Passport Renewals",
      "Indian Visa Applications",
      "Police Clearance Certificates (PCC)",
      "Surrender Certificate Applications",
    ],
  },
  {
    heading: "Business & Corporate Services",
    items: [
      "Sponsor Licence Application Administration",
      "Employee Visa Documentation",
      "Corporate Relocation Support",
      "Business Travel Documentation",
      "Document Organisation & Application Preparation",
    ],
  },
  {
    heading: "Global Services",
    items: [
      "Administrative support for a wide range of international visa and travel documentation.",
    ],
  },
];

const whyChoose = [
  "Over 20 Years of Experience",
  "Trusted Since 2005",
  "Boutique Client Experience",
  "Precision in Every Detail",
  "Confidential & Professional Service",
  "Flexible Appointment Options",
  "UK & International Clients Welcome",
];

const values = [
  { name: "Integrity", body: "Honest, transparent communication." },
  { name: "Excellence", body: "Delivering premium service with meticulous attention to detail." },
  { name: "Trust", body: "Building long-term relationships through reliability and confidentiality." },
  { name: "Personal Service", body: "Every client receives tailored administrative support." },
];

const CARD_HOVER = { y: -4 };
const CARD_HOVER_TRANSITION = { duration: 0.4 };

export default function About() {
  return (
    <div className="relative bg-navy min-h-screen" data-testid="about-page">
      <div className="grain" />
      <Nav />

      <main className="pt-32 md:pt-40">
        {/* Masthead */}
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16 md:pb-24">
          <div className="grid grid-cols-12 gap-8 md:gap-16 items-end">
            <ScrollReveal className="col-span-12 md:col-span-8">
              <h1 className="font-display font-light text-ivory text-6xl md:text-8xl lg:text-9xl leading-[0.85] tracking-tight">
                Crafting your<br />
                <span className="italic text-gold">global journey.</span>
              </h1>
            </ScrollReveal>
            <ScrollReveal className="col-span-12 md:col-span-4" delay={0.15}>
              <div className="font-mono-ui text-xs tracking-[0.3em] uppercase text-ivory/60 mb-3">Est. 2005</div>
              <p className="text-ivory/70 text-base md:text-lg leading-relaxed">
                A private practice for professional administrative
                application support. UK. Indian. Corporate. Global.
              </p>
            </ScrollReveal>
          </div>
        </section>

        {/* About body */}
        <section className="bg-navy-800 border-y border-ivory/10 py-20 md:py-32">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8 md:gap-16">
            <ScrollReveal className="col-span-12 md:col-span-4">
              <h2 className="font-display italic text-4xl md:text-5xl text-gold leading-tight">
                Every application <br /> tells a story.
              </h2>
            </ScrollReveal>
            <ScrollReveal className="col-span-12 md:col-span-8" delay={0.1}>
              <p className="text-ivory/85 text-lg md:text-xl leading-relaxed italic">
                Since 2005, The Visa Atelier has believed every application
                tells a story. Whether you&rsquo;re reuniting with loved ones,
                pursuing new opportunities abroad, expanding your business
                internationally, or securing your future, your journey
                deserves exceptional care and attention; which is why we
                were founded on the principles of professionalism, precision,
                and personalised service. With over two decades of industry
                experience, we&rsquo;ve helped individuals, families, and
                businesses prepare their applications with confidence,
                offering a premium administrative support experience at every
                step.
              </p>
            </ScrollReveal>
          </div>
        </section>

        {/* Expertise */}
        <section className="py-20 md:py-32" data-testid="about-expertise">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <ScrollReveal>
              <h2 className="font-display font-light text-ivory text-5xl md:text-7xl leading-[0.9] tracking-tight mb-16">
                Our <span className="italic text-gold">expertise.</span>
              </h2>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-14">
              {expertise.map((e, idx) => (
                <ScrollReveal key={e.heading} delay={idx * 0.08}>
                  <div className="border-t border-gold/40 pt-6">
                    <div className="flex items-baseline justify-between mb-6">
                      <h3 className="font-display text-3xl md:text-4xl text-ivory tracking-tight">
                        {e.heading}
                      </h3>
                      <span className="font-mono-ui text-xs text-gold/60 tabular-nums">
                        0{idx + 1}
                      </span>
                    </div>
                    <ul className="space-y-3">
                      {e.items.map((it) => (
                        <li
                          key={it}
                          className="flex items-start gap-4 py-3 border-b border-ivory/10"
                        >
                          <span className="font-mono-ui text-[10px] text-gold/60 uppercase tracking-widest mt-1 min-w-[20px]">→</span>
                          <span className="text-ivory/85 text-base md:text-lg">{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose */}
        <section className="bg-navy-800 border-y border-ivory/10 py-20 md:py-32">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <ScrollReveal>
              <h2 className="font-display font-light text-ivory text-5xl md:text-7xl leading-[0.9] tracking-tight mb-16">
                Why choose <br /><span className="italic text-gold">The Visa Atelier.</span>
              </h2>
            </ScrollReveal>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {whyChoose.map((w, idx) => (
                <ScrollReveal key={w} delay={idx * 0.06}>
                  <motion.div
                    whileHover={CARD_HOVER}
                    transition={CARD_HOVER_TRANSITION}
                    className="border border-ivory/10 hover:border-gold/60 p-8 h-full flex items-start gap-6 transition-colors duration-500"
                    data-testid={`why-choose-${idx}`}
                  >
                    <span className="font-mono-ui text-sm text-gold tabular-nums">0{idx + 1}</span>
                    <span className="font-display text-2xl md:text-3xl text-ivory leading-tight">
                      {w}
                    </span>
                  </motion.div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 md:py-32">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <ScrollReveal>
              <h2 className="font-display font-light text-ivory text-5xl md:text-7xl leading-[0.9] tracking-tight mb-16">
                Our <span className="italic text-gold">values.</span>
              </h2>
            </ScrollReveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
              {values.map((v, idx) => (
                <ScrollReveal key={v.name} delay={idx * 0.08}>
                  <div className="border-l-2 border-gold pl-6 md:pl-8">
                    <div className="font-display italic text-4xl md:text-5xl text-gold">{v.name}</div>
                    <p className="text-ivory/80 text-lg md:text-xl mt-4 leading-relaxed">
                      {v.body}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Promise */}
        <section className="bg-navy-800 border-y border-ivory/10 py-20 md:py-32">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8 md:gap-16">
            <ScrollReveal className="col-span-12 md:col-span-4">
              <h2 className="font-display italic text-4xl md:text-5xl text-ivory leading-tight">
                Prepared with <br />care, always.
              </h2>
            </ScrollReveal>
            <ScrollReveal className="col-span-12 md:col-span-8" delay={0.1}>
              <p className="text-ivory/85 text-xl md:text-2xl leading-relaxed font-display italic">
                &ldquo;We are committed to delivering a professional,
                organised and client-focused experience. Every application
                is prepared with care, precision and attention to detail,
                ensuring you feel supported throughout your journey.&rdquo;
              </p>
            </ScrollReveal>
          </div>
        </section>

        {/* Important information */}
        <section className="py-20 md:py-28">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <ScrollReveal>
              <div className="border border-ivory/10 p-8 md:p-12">
                <div className="font-mono-ui text-xs tracking-[0.3em] text-gold uppercase mb-4">Important Information</div>
                <p className="text-ivory/75 text-base md:text-lg leading-relaxed">
                  The Visa Atelier provides professional administrative
                  application support. We assist clients with organising
                  documentation, preparing application forms using
                  information supplied by the client, document checking and
                  appointment booking. Where regulated UK immigration
                  advice or legal representation is required, clients
                  should seek assistance from an appropriately authorised
                  immigration adviser or solicitor.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 md:py-28 border-t border-ivory/10">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div>
              <div className="font-mono-ui text-xs tracking-[0.35em] text-gold uppercase mb-3">Ready to begin?</div>
              <h2 className="font-display font-light text-ivory text-4xl md:text-6xl leading-[0.95] tracking-tight">
                Let us craft your <span className="italic text-gold">journey.</span>
              </h2>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/#book"
                className="inline-flex items-center gap-4 px-8 py-4 bg-gold text-navy font-mono-ui text-sm tracking-[0.25em] uppercase hover:bg-ivory transition-colors"
                data-testid="about-cta-enquire"
              >
                Send an Enquiry →
              </Link>
              <Link
                to="/#consultation"
                className="inline-flex items-center gap-4 px-8 py-4 border border-gold text-gold font-mono-ui text-sm tracking-[0.25em] uppercase hover:bg-gold hover:text-navy transition-colors"
                data-testid="about-cta-consult"
              >
                Book Consultation
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
